// src/utils/GalleryUseUpload.js
import { useCallback, useEffect, useState } from "react";
import adminAPI from "../api/apiAdmin";

/* =========================================================
   UPLOAD HOOK — uploads a single file to the gallery endpoint
   and returns the created gallery item.
========================================================= */
function useUpload() {
  const [loading, setLoading] = useState(false);

  const upload = useCallback(async ({ file }) => {
    if (!file) return { error: "No file provided" };

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

    if (!isImage && !isVideo) {
      return { error: "Only image or video files are allowed" };
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setLoading(true);

      const response = await adminAPI.post("/admin/gallery/upload", formData);
      const data = response?.data;

      if (!data?.id) {
        return { error: "Invalid upload response" };
      }

      return {
        id: data.id,
        url: data.url || "",
        mime_type: file.type,
        type: data.type || (isImage ? "image" : "video"),
        size: file.size,
        status: data.status || "ready",
      };
    } catch (error) {
      return {
        error:
          error?.response?.data?.message ||
          error?.message ||
          "Upload failed",
      };
    } finally {
      setLoading(false);
    }
  }, []);

  return [upload, { loading }];
}

/* =========================================================
   GALLERY DATA HOOK — full CRUD against /admin/gallery
========================================================= */
function useGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchGallery = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.get("/admin/gallery");
      const galleryData = response?.data?.data || response?.data || [];
      setItems(Array.isArray(galleryData) ? galleryData : []);
    } catch (error) {
      console.error("Failed to fetch gallery", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const createItem = useCallback(async (payload) => {
    // payload is expected to have { file, title? } OR { url, type, ... }
    // If a File object is present, send as FormData; otherwise send JSON.
    let response;

    if (payload instanceof FormData) {
      response = await adminAPI.post("/admin/gallery", payload);
    } else if (payload.file instanceof File) {
      const fd = new FormData();
      fd.append("file", payload.file);
      if (payload.title) fd.append("title", payload.title);
      response = await adminAPI.post("/admin/gallery", fd);
    } else {
      // Fallback: the upload hook already created the item via /upload,
      // so we just need to refresh the list.
      await fetchGallery();
      return payload;
    }

    const created = response?.data?.data || response?.data;
    setItems((prev) => [created, ...prev]);
    return created;
  }, [fetchGallery]);

  const replaceItem = useCallback(async (id, payload) => {
    let response;

    if (payload instanceof FormData) {
      response = await adminAPI.put(`/admin/gallery/${id}`, payload);
    } else if (payload.file instanceof File) {
      const fd = new FormData();
      fd.append("file", payload.file);
      if (payload.title) fd.append("title", payload.title);
      response = await adminAPI.put(`/admin/gallery/${id}`, fd);
    } else {
      // Legacy path: send raw payload as FormData with url
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        if (v !== undefined && v !== null) fd.append(k, v);
      });
      response = await adminAPI.put(`/admin/gallery/${id}`, fd);
    }

    const updated = response?.data?.data || response?.data;
    setItems((prev) =>
      prev.map((item) => (item.id === id ? updated : item))
    );
    return updated;
  }, []);

  const deleteItem = useCallback(async (id) => {
    await adminAPI.delete(`/admin/gallery/${id}`);
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const checkStatus = useCallback(async (id) => {
    try {
      const response = await adminAPI.get(`/admin/gallery/${id}`);
      const item = response?.data?.data || response?.data;

      // Update the item in the list if it exists
      setItems((prev) =>
        prev.map((i) => (i.id === id ? item : i))
      );

      return item;
    } catch (error) {
      console.error("Failed to check gallery item status", error);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  return {
    items,
    loading,
    refresh: fetchGallery,
    checkStatus,
    createItem,
    replaceItem,
    deleteItem,
  };
}

export { useUpload, useGallery };
export default useUpload;
